import { Card, Progress, Alert } from '@/components/ui'
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import type { UniqueIdentifier, ImportExecuteResponse } from '@/pages/events/attendees/types'

interface ReviewStepProps {
  totalRows: number
  uniqueIdentifier: UniqueIdentifier
  mappedCount: number
  skippedCount: number
  updateExisting: boolean
  importTag: string
  sendConfirmation: boolean
  isProcessing: boolean
  progressPercent: number
  importResult: ImportExecuteResponse | null
}

const IDENTIFIER_LABELS: Record<UniqueIdentifier, string> = {
  email: 'Email',
  attendee_id: 'Attendee ID',
  none: 'None (all new)',
}

export default function ReviewStep({
  totalRows,
  uniqueIdentifier,
  mappedCount,
  skippedCount,
  updateExisting,
  importTag,
  sendConfirmation,
  isProcessing,
  progressPercent,
  importResult,
}: ReviewStepProps) {
  if (importResult) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <CheckCircleOutlined style={{ fontSize: '48px', color: '#22c55e', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
            Import Complete
          </h3>
          <p style={{ color: '#6b7280' }}>
            Your attendees have been imported successfully.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          <Card style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#16a34a' }}>
              {importResult.created_count || 0}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Created</div>
          </Card>
          <Card style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#2563eb' }}>
              {importResult.updated_count || 0}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Updated</div>
          </Card>
          <Card style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#9ca3af' }}>
              {importResult.skipped_count || 0}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Skipped</div>
          </Card>
          <Card style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#dc2626' }}>
              {importResult.error_count || 0}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Errors</div>
          </Card>
        </div>

        {importResult.errors_data && importResult.errors_data.length > 0 && (
          <Alert
            type="warning"
            message={`${importResult.errors_data.length} rows had errors`}
            description={
              <ul style={{ listStyleType: 'disc', listStylePosition: 'inside', marginTop: '8px', maxHeight: '128px', overflowY: 'auto' }}>
                {importResult.errors_data.slice(0, 10).map((error, index) => (
                  <li key={index} style={{ fontSize: '14px' }}>
                    {error.row ? `Row ${error.row}: ` : ''}{error.message}
                  </li>
                ))}
                {importResult.errors_data.length > 10 && (
                  <li style={{ fontSize: '14px', color: '#6b7280' }}>
                    ... and {importResult.errors_data.length - 10} more errors
                  </li>
                )}
              </ul>
            }
            showIcon
          />
        )}
      </div>
    )
  }

  if (isProcessing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <LoadingOutlined style={{ fontSize: '48px', color: '#3b82f6', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
            Importing Attendees
          </h3>
          <p style={{ color: '#6b7280' }}>
            Please wait while we process your import...
          </p>
        </div>

        <Progress percent={progressPercent} status="active" />

        <p style={{ textAlign: 'center', fontSize: '14px', color: '#6b7280' }}>
          This may take a few minutes for large files.
        </p>
      </div>
    )
  }

  const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }
  const lastRowStyle = { display: 'flex', justifyContent: 'space-between', padding: '8px 0' }
  const labelStyle = { color: '#6b7280' }
  const valueStyle = { fontWeight: 500 }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
          Review and confirm import
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
          Please review the import settings before proceeding.
        </p>
      </div>

      <Card style={{ padding: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={rowStyle}>
            <span style={labelStyle}>Contacts to be processed</span>
            <span style={valueStyle}>{totalRows}</span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Unique identifier</span>
            <span style={valueStyle}>{IDENTIFIER_LABELS[uniqueIdentifier]}</span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Columns to import</span>
            <span style={valueStyle}>
              {mappedCount} <span style={{ color: '#9ca3af' }}>({skippedCount} skipped)</span>
            </span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Update existing contacts</span>
            <span style={valueStyle}>{updateExisting ? 'Yes' : 'No'}</span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Import tag</span>
            <span style={valueStyle}>{importTag}</span>
          </div>

          <div style={lastRowStyle}>
            <span style={labelStyle}>Send confirmation email</span>
            <span style={valueStyle}>{sendConfirmation ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </Card>

      <div style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '16px' }}>
        <p style={{ fontSize: '14px', color: '#854d0e' }}>
          <strong>Ready to import?</strong> Click "Complete Import" to start processing your attendees.
          {totalRows > 100 && ' Large imports will be processed in the background.'}
        </p>
      </div>
    </div>
  )
}
